/*
 * Copyright (c) 2018. Unconventional Software Corporation
 */

/**
 * Created by andrea on 2017-06-19.
 */

export class DataReference {

  private static ACTION_VALUE: string = 'value';
  private static ACTION_CHILD_CHANGED: string = 'child_changed';
  private static ACTION_CHILD_ADDED: string = 'child_added';
  private static ACTION_CHILD_REMOVED: string = 'child_removed';

  private static USERS: string = 'users';
  private static USER_PUSH_TOKEN: string = 'pushToken';
  private static USER_UUID: string = 'uuid';
  private static USER_OBJECTS: string = 'userObjects';
  private static FOLLOWING: string = 'following';
  private static DANCERS: string = 'dancers';
  private static STUDIOS: string = 'studios';
  private static EVENTS_PURCHASED: string = 'eventsPurchased';
  private static EVENTS: string = 'events';
  private static EVENT_CODES: string = 'eventCodes';
  private static COMPETITIONS: string = 'competitions';
  private static TOURS: string = 'tours';
  private static NAMES: string = 'names';
  private static VENUES: string = 'venues';
  private static COMPETITION_OBJECTS: string = 'competitionObjects';
  private static METADATA: string = 'metadata';
  private static EVENT_OBJECTS: string = 'eventObjects';
  private static ENTRIES: string = 'entries';
  private static ADJUDICATORS: string = 'judges';
  private static DANCERS_FOLLOWING = 'dancersFollowing';
  private static STUDIOS_FOLLOWING = 'studiosFollowing';
  private static PURCHASES = 'purchases';
  static readonly ENTRY_STATUS = 'entryStatus';
  private static readonly SYSTEM = 'system';

  static readonly ANNOUNCEMENT_STATUS = 'status';

  static readonly TIMESTAMP = 'timestamp';

  private static EVENT_ENTRY_SCORES = 'scores';
  private static EVENT_AUTHORIZATION_QUEUE = 'authorizationMessage';
  private static readonly EVENT_AWARDS = 'entryAwards';
  static readonly PUBLISHED_KEY = 'published';
  static readonly PUBLISHED_VALUE = 1;

  private static readonly VOTING_OPTIONS = 'votingOptions';
  private static readonly VOTING_RESULTS = 'votingResults';
  static readonly VOTING_IS_ACTIVE = 'isOpen';
  static readonly VOTING_IS_VISIBLE = 'isVisible';

  static get Value(): string {
    return DataReference.ACTION_VALUE;
  }

  static get Change(): string {
    return DataReference.ACTION_CHILD_CHANGED;
  }

  static get Add(): string {
    return DataReference.ACTION_CHILD_ADDED;
  }

  static get Remove(): string {
    return DataReference.ACTION_CHILD_REMOVED;
  }

  // region User References

  static getUserRef(userId: string) {
    return `${this.USERS}/${userId}`;
  }

  static getUserPushTokenRef(userId: string) {
    return `${this.getUserRef(userId)}/${this.USER_PUSH_TOKEN}`;
  }

  static getUserUuidRef(userId: string) : string {
    return `${this.getUserRef(userId)}/${this.USER_UUID}`;
  }

  static getUserDevicesRef(userId: string) : string {
    return `${DataReference.USER_OBJECTS}/devices/${userId}`;
  }

  // endregion

  // region Following References

  static getEntitiesFollowingRef(userId: string) : string {
    return `${DataReference.USER_OBJECTS}/${DataReference.FOLLOWING}/${userId}`;
  }

  static getDancersFollowingRef(userId: string): string {
    return `${DataReference.USER_OBJECTS}/${DataReference.FOLLOWING}/${userId}/${DataReference.DANCERS}`;
  }

  static getStudiosFollowingRef(userId: string): string {
    return `${DataReference.USER_OBJECTS}/${DataReference.FOLLOWING}/${userId}/${DataReference.STUDIOS}`;
  }

  static getEventsPurchasedRef(userId: string): string {
    return `${DataReference.USER_OBJECTS}/${DataReference.EVENTS_PURCHASED}/${userId}`;
  }

  static getEventAuthorizationRef(userId: string, deviceId: string): string {
    return `${DataReference.USER_OBJECTS}/${this.EVENT_AUTHORIZATION_QUEUE}/${userId}/${deviceId}`;
  }

  static getEventDancersFollowingRef(eventKey: string, studioKey: string, dancerName: string, userId: string = '') : string {
    return `${this.EVENT_OBJECTS}/${this.DANCERS_FOLLOWING}/${eventKey}/${studioKey}/${this.convertDancerNameToFirebaseKey(dancerName)}/${userId}`;
  }

  static getEventStudiosFollowingRef(eventKey: string, studioKey: string, userId: string = '') : string {
    return `${this.EVENT_OBJECTS}/${this.STUDIOS_FOLLOWING}/${eventKey}/${studioKey}/${userId}`;
  }

  static getGeneralStudiosFollowingRef(studioKey: string, userKey: string) : string {
    return `${this.FOLLOWING}/${this.STUDIOS}/${studioKey}/${userKey}`;
  }

  static getEventPushTokenRef(userKey: string, eventKey: string) : string {
    return `${this.EVENT_OBJECTS}/${this.PURCHASES}/${eventKey}/${userKey}`;
  }

  // endregion

  static getEventsRef(eventId: string) : string {
    return `${DataReference.EVENTS}/${eventId}`;
  }

  static getCompetitionsRef() : string {
    return `${DataReference.COMPETITIONS}`;
  }

  static getCompetitionRef(compId: string) : string {
    return `${DataReference.COMPETITIONS}/${compId}`;
  }

  static getVenuesRef(venueId: string) {
    return `${DataReference.VENUES}/${venueId}`;
  }

  static getToursRef(compId: string) {
    return `${DataReference.COMPETITION_OBJECTS}/${DataReference.TOURS}/${DataReference.NAMES}/${compId}`;
  }

  static getAdjudicators(eventId: string) : string {
    return `${DataReference.EVENT_OBJECTS}/${this.ADJUDICATORS}/${eventId}`;
  }

  static getCompetitionMetadataRef(compId: string, tourId: string, language: string) {
    return `${DataReference.COMPETITION_OBJECTS}/${this.METADATA}/${compId}/${tourId}/${language}`;
  }

  static getEntriesRef(eventId: string) {
    return `${DataReference.EVENT_OBJECTS}/${DataReference.ENTRIES}/${eventId}`;
  }

  static getEventStudiosRef(eventId: string) {
    return `${DataReference.EVENT_OBJECTS}/${DataReference.STUDIOS}/${eventId}`;
  }

  private static convertDancerNameToFirebaseKey(name: string) : string {
    return this.toCleanUpper(name).replace(/\s/g, '_');
  }

  private static toCleanUpper(val: string) : string{
    return val.trim().toUpperCase();
  }

  static getContactUsUrl(lang: string = 'en') : string {
    return `${DataReference.SYSTEM}/${lang}/contactUsUrl`;
  }

  // region Awards

  static getScoresForEvent(eventId: string) : string {
    return `${this.EVENT_OBJECTS}/${this.EVENT_ENTRY_SCORES}/${eventId}`;
  }

  static getAwardsForEventRef(eventId: string) : string {
    return `${this.EVENT_OBJECTS}/${this.EVENT_AWARDS}/${eventId}`;
  }

  // endregion

  // region Voting

  static getVotingOptionsForEventRef(eventId: string) : string {
    return `${this.EVENT_OBJECTS}/${this.VOTING_OPTIONS}/${eventId}`;
  }

  static getEventVoteRef(votingId: string, eventId: string, entryId: string, userId: string) {
    return `${this.EVENT_OBJECTS}/${this.VOTING_RESULTS}/${eventId}/${votingId}/${entryId}/${userId}`;
  }

  // endregion

  // region Firebase System

  public static readonly CONNECTED_REF = '.info/connected';

  static getUserOnlineRef(userId: string) : string {
    return `${DataReference.USER_OBJECTS}/meta/onlineState/${userId}`;
  }

  static getHelpTopicsRef(typeId: string, lang: string = 'en') {
    return `${DataReference.SYSTEM}/help/${lang}/${typeId}`;
  }

  // endregion

  static getEventForCode(code: string) {
    return `${DataReference.EVENT_CODES}/${code}`;
  }

  static getTourEventsKeysRef(compId: string, tourId: string) : string {
    return `${DataReference.COMPETITION_OBJECTS}/${DataReference.TOURS}/${DataReference.EVENTS}/${compId}/${tourId}`;
  }

  static getAnnouncementsForEvent(id: string) : string {
    return `${DataReference.EVENT_OBJECTS}/feed/${id}`;
  }

  // region Messaging

  static readonly CONNECTIONS: string = 'connections';
  static readonly CONNECTION_REQUESTS_SENT = 'connectionRequestsSent';
  static readonly CONNECTION_REQUESTS_RECEIVED = 'connectionRequestsReceived';
  static readonly CONVERSATIONS: string = 'conversations';
  static readonly CONVERSATION_OBJECTS: string = 'conversationObjects';
  static readonly PARTICIPANT_ID: string = 'participantId';
  static readonly MESSAGES: string = 'messages';
  static readonly GROUPS: string = 'groups';
  static readonly GROUP_OBJECTS: string = 'groupObjects';
  static readonly PARTICIPANTS: string = 'participants';
  static readonly EVENT_ID: string = 'eventId';
  static readonly CHAT_ACCESSIBILITY: string = 'accessibility';
  private static readonly SERVER_PROCESSING = 'serverProcessing';


  static getFeaturesForEvent(eventId: string) {
    return `${DataReference.EVENT_OBJECTS}/publicFeatures/${eventId}`;
  }

  static getUserPublicProfileRef(userId: string): string {
    return `${DataReference.USER_OBJECTS}/publicProfile/${userId}`;
  }

  static getConnectionRequestsSentForUser(userId: string) : string {
    return `${DataReference.USER_OBJECTS}/${DataReference.CONNECTION_REQUESTS_SENT}/${userId}`;
  }

  static getEventParticipantsRef(eventId: string) : string {
    return `${DataReference.EVENT_OBJECTS}/eventParticipants/${eventId}`;
  }

  static getAllConnectionsForUserRef(userId: string) : string {
    return `${DataReference.USER_OBJECTS}/${DataReference.CONNECTIONS}/${userId}`;
  }

  static getConnectionRequestsReceivedForUser(userId: string) : string {
    return `${DataReference.USER_OBJECTS}/${DataReference.CONNECTION_REQUESTS_RECEIVED}/${userId}`;
  }

  static getEventConversations(eventId: string) : string {
    return `${DataReference.EVENT_OBJECTS}/${DataReference.CONVERSATIONS}/${eventId}`;
  }

  static getConversationsForUser(userId: string) : string {
    return `${DataReference.USER_OBJECTS}/${DataReference.CONVERSATIONS}/${userId}`;
  }

  static getConversationsRef() : string {
    return DataReference.CONVERSATIONS;
  }

  static getConversationWithParticipant(userId: string, participantId: string) : string {
    return `${DataReference.getConversationsForUser(userId)}/${participantId}`;
  }

  static getConversationMessagesRef(): string {
    return `${DataReference.CONVERSATION_OBJECTS}/${DataReference.MESSAGES}`;
  }

  static getMessagesForConversation(conversationId: string) : string {
    return `${DataReference.getConversationMessagesRef()}/${conversationId}`;
  }

  static getEventConversationsForUser(eventId: string, userId: string) : string {
    return `${DataReference.getEventConversations(eventId)}/${userId}`;
  }

  static getMessageForConversation(conversationId: string, messageId: string) : string {
    return `${DataReference.getMessagesForConversation(conversationId)}/${messageId}`;
  }

  static getServerProcessingMessage(eventId: string, messageId: string) {
    return `${DataReference.SERVER_PROCESSING}/${DataReference.MESSAGES}/${eventId}/${messageId}`;
  }

  static getServerProcessingGroupMessage(eventId: string, messageId: string) {
    return `${DataReference.SERVER_PROCESSING}/groupMessages/${eventId}/${messageId}`;
  }

  static getServerProcessingRequestSent(eventId: string) : string {
    return `${DataReference.SERVER_PROCESSING}/connectionRequests/${eventId}`;
  }

  static getGroupsForUser(userId: string) : string {
    return `${DataReference.USER_OBJECTS}/${DataReference.GROUPS}/${userId}`;
  }

  static getGroup(groupId: string) : string {
    return `${DataReference.GROUPS}/${groupId}`;
  }

  static getEventGroups(eventId: string) : string {
    return `${DataReference.EVENT_OBJECTS}/${DataReference.GROUPS}/${eventId}`;
  }

  static getGroupParticipantsRef(groupId: string) : string {
    return `${DataReference.GROUP_OBJECTS}/${DataReference.PARTICIPANTS}/${groupId}`;
  }

  static getGroupMessagesRef(groupId: string) : string {
    return `${DataReference.GROUP_OBJECTS}/${DataReference.MESSAGES}/${groupId}`;
  }

  static getConnectionForUserRef(userId: string, connectionId: string) {
    return `${DataReference.getAllConnectionsForUserRef(userId)}/${connectionId}`;
  }

  // endregion
}

export enum EntryStatus {
  Planning, Confirmed, Active, Complete, OnHold, Cancelled, Adjudicated, AdjudicatedPublished, Featured
}

export enum OnlineState {
  Offline = 0, Online = 1
}


// export const ScheduleStatus = {
//   Planning: 0,
//   Confirmed: 1,
//   Active: 2,
//   Complete: 3,
//   OnHold: 4,
//   Cancelled: 5
// };



// WEBPACK FOOTER //
// ./src/shared/DataReference.ts